import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import {
  setDeliveryMethod,
  setShippingData,
  setCheckoutStep,
} from "../../../redux/checkout/action";
import DeliveryForm from "./DeliveryForm";
import PickupForm from "./PickupForm";

function ShippingStep(props) {
  const { checkout, authJWT, groceryList } = props;
  const {
    deliveryMethod,
    shippingData,
    shippingAvailable,
    selectedStore,
    pickupDate,
    selectedSlot,
  } = checkout;
  const { user } = authJWT;

  // Abilitazione metodi da azienda/get-info (fallback: entrambi).
  const ecommerceConfig = checkout.ecommerceConfig || {};
  const pickupEnabled = ecommerceConfig.pickupEnabled !== false;
  const deliveryEnabled = ecommerceConfig.deliveryEnabled !== false;
  const bothEnabled = pickupEnabled && deliveryEnabled;
  const configLoaded = !!ecommerceConfig.loaded;

  const [errors, setErrors] = useState({});

  // Pre-fill from user profile on mount
  useEffect(() => {
    if (user) {
      const prefill = {};
      if (!shippingData.nome && user.name) prefill.nome = user.name;
      if (!shippingData.cognome && user.surname) prefill.cognome = user.surname;
      if (!shippingData.email && user.email) prefill.email = user.email;
      if (!shippingData.telefono && user.phone) prefill.telefono = user.phone;
      if (!shippingData.indirizzo && user.address) prefill.indirizzo = user.address;
      if (!shippingData.citta && user.city) prefill.citta = user.city;
      if (!shippingData.cap && user.cap) prefill.cap = user.cap;
      if (Object.keys(prefill).length > 0) {
        props.setShippingData(prefill);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Se il tenant abilita un solo metodo, forzalo e nascondi la scelta.
  useEffect(() => {
    if (pickupEnabled && !deliveryEnabled && deliveryMethod !== "pickup") {
      props.setDeliveryMethod("pickup");
    } else if (deliveryEnabled && !pickupEnabled && deliveryMethod !== "delivery") {
      props.setDeliveryMethod("delivery");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickupEnabled, deliveryEnabled]);

  const handleMethodChange = (method) => {
    props.setDeliveryMethod(method);
    setErrors({});
  };

  const handleFieldChange = (field) => (e) => {
    props.setShippingData({ [field]: e.target.value });
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const validate = () => {
    const errs = {};

    if (!shippingData.nome || !shippingData.nome.trim()) {
      errs.nome = "Nome obbligatorio";
    }
    if (!shippingData.cognome || !shippingData.cognome.trim()) {
      errs.cognome = "Cognome obbligatorio";
    }
    if (!shippingData.email || !shippingData.email.trim()) {
      errs.email = "Email obbligatoria";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(shippingData.email.trim())) {
      errs.email = "Email non valida";
    }

    // Accetta prefisso internazionale e separatori comuni (+ - / ( ) . spazi):
    // conta solo le cifre effettive (9-15). Evita falsi negativi su numeri validi
    // tipo "+39 333 123-4567".
    const rawPhone = (shippingData.telefono || "").trim();
    const phoneDigits = rawPhone.replace(/\D/g, "");
    if (!phoneDigits) {
      errs.telefono = "Telefono obbligatorio";
    } else if (
      !/^[+\d\s().\/-]+$/.test(rawPhone) ||
      phoneDigits.length < 9 ||
      phoneDigits.length > 15
    ) {
      errs.telefono = "Numero di telefono non valido (9-15 cifre)";
    }

    if (deliveryMethod === "delivery") {
      if (!shippingData.indirizzo || !shippingData.indirizzo.trim()) {
        errs.indirizzo = "Indirizzo obbligatorio";
      }
      if (!shippingData.citta || !shippingData.citta.trim()) {
        errs.citta = "Città obbligatoria";
      }
      if (!shippingData.cap || !shippingData.cap.trim()) {
        errs.cap = "CAP obbligatorio";
      } else if (!/^\d{5}$/.test(shippingData.cap.trim())) {
        errs.cap = "CAP non valido (5 cifre)";
      }
      if (shippingAvailable !== true) {
        // Cause tipiche: CAP e città non coincidono, oppure il costo di
        // spedizione non è ancora stato calcolato per questo indirizzo.
        errs.cap =
          errs.cap ||
          "Controlla CAP e città: spedizione non disponibile o non ancora calcolata per questo indirizzo.";
      }
    }

    if (deliveryMethod === "pickup") {
      if (!selectedStore) {
        errs.selectedStore = "Seleziona un negozio";
      }
      if (!pickupDate) {
        errs.pickupDate = "Seleziona una data";
      }
      if (!selectedSlot) {
        errs.selectedSlot = "Seleziona un orario";
      }
    }

    return errs;
  };

  const handleContinue = () => {
    if (!deliveryMethod) {
      setErrors({ deliveryMethod: "Seleziona un metodo di consegna" });
      return;
    }
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    props.setCheckoutStep(3);
  };

  const handleBack = () => {
    props.setCheckoutStep(1);
  };

  // Finché la config e-commerce (get-info) non è caricata non mostriamo la scelta
  // del metodo: evita che un tenant pickup-only/delivery-only mostri per un attimo
  // entrambi i metodi e l'utente selezioni quello sbagliato.
  if (!configLoaded) {
    return (
      <div className="co-card">
        <div style={{ padding: "40px 0", textAlign: "center", color: "#888" }}>
          Caricamento opzioni di consegna…
        </div>
      </div>
    );
  }

  return (
    <div className="co-card">
      <div className="co-card__header">
        <h2 className="co-card__title">Consegna e dati personali</h2>
      </div>

      {/* Delivery method selection */}
      <div className="co-form__group">
        <label className="co-form__label">Metodo di consegna</label>
        {bothEnabled ? (
          <div className="co-form__radio">
            <div
              className={
                "co-form__radio-item" +
                (deliveryMethod === "delivery" ? " co-form__radio-item--selected" : "")
              }
              onClick={() => handleMethodChange("delivery")}
            >
              <span className={
                "co-radio-dot" +
                (deliveryMethod === "delivery" ? " co-radio-dot--active" : "")
              } />
              Consegna a domicilio
            </div>
            <div
              className={
                "co-form__radio-item" +
                (deliveryMethod === "pickup" ? " co-form__radio-item--selected" : "")
              }
              onClick={() => handleMethodChange("pickup")}
            >
              <span className={
                "co-radio-dot" +
                (deliveryMethod === "pickup" ? " co-radio-dot--active" : "")
              } />
              Ritiro in negozio
            </div>
          </div>
        ) : (pickupEnabled || deliveryEnabled) ? (
          // Un solo metodo abilitato dal tenant: mostralo fisso, non selezionabile.
          <div className="co-form__radio">
            <div className="co-form__radio-item co-form__radio-item--selected">
              <span className="co-radio-dot co-radio-dot--active" />
              {deliveryEnabled ? "Consegna a domicilio" : "Ritiro in negozio"}
            </div>
          </div>
        ) : null}
        {errors.deliveryMethod && (
          <span style={{ color: "#dc3545", fontSize: "0.82rem", marginTop: 4, display: "block" }}>
            {errors.deliveryMethod}
          </span>
        )}
      </div>

      {/* Conditional form */}
      {deliveryMethod === "delivery" && <DeliveryForm />}
      {deliveryMethod === "pickup" && <PickupForm />}

      {/* Personal data */}
      {deliveryMethod && (
        <div style={{ marginTop: 20 }}>
          <label className="co-form__label" style={{ marginBottom: 10 }}>
            Dati personali
          </label>

          <div className="co-form__row">
            <div className="co-form__group">
              <label className="co-form__label">Nome</label>
              <input
                className="co-form__input"
                type="text"
                value={shippingData.nome}
                onChange={handleFieldChange("nome")}
                placeholder="Nome"
              />
              {errors.nome && (
                <span
                  style={{ color: "#dc3545", fontSize: "0.82rem", display: "block", marginTop: 2 }}
                >
                  {errors.nome}
                </span>
              )}
            </div>
            <div className="co-form__group">
              <label className="co-form__label">Cognome</label>
              <input
                className="co-form__input"
                type="text"
                value={shippingData.cognome}
                onChange={handleFieldChange("cognome")}
                placeholder="Cognome"
              />
              {errors.cognome && (
                <span
                  style={{ color: "#dc3545", fontSize: "0.82rem", display: "block", marginTop: 2 }}
                >
                  {errors.cognome}
                </span>
              )}
            </div>
          </div>

          <div className="co-form__group">
            <label className="co-form__label">Email</label>
            <input
              className="co-form__input"
              type="email"
              value={shippingData.email}
              onChange={handleFieldChange("email")}
              placeholder="email@esempio.com"
            />
            {errors.email && (
              <span
                style={{ color: "#dc3545", fontSize: "0.82rem", display: "block", marginTop: 2 }}
              >
                {errors.email}
              </span>
            )}
          </div>

          <div className="co-form__group">
            <label className="co-form__label">Telefono</label>
            <input
              className="co-form__input"
              type="tel"
              value={shippingData.telefono}
              onChange={handleFieldChange("telefono")}
              placeholder="Numero di telefono"
            />
            {errors.telefono && (
              <span
                style={{ color: "#dc3545", fontSize: "0.82rem", display: "block", marginTop: 2 }}
              >
                {errors.telefono}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Navigation buttons */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: 20,
        }}
      >
        <button className="up-btn up-btn--ghost" onClick={handleBack}>
          Indietro
        </button>
        <button className="up-btn up-btn--primary" onClick={handleContinue}>
          Continua
        </button>
      </div>
    </div>
  );
}

const mapStateToProps = (state) => ({
  checkout: state.checkout,
  authJWT: state.authJWT,
  groceryList: state.groceryList,
});

export default connect(mapStateToProps, {
  setDeliveryMethod,
  setShippingData,
  setCheckoutStep,
})(ShippingStep);
